// The list beside the timeline: "To do" (before the day, the day, after it, no
// date) and "Done", with cards that light their blocks on the timeline when
// pressed, open their Case from a button at their right, get a day from "…",
// and are carried onto the timeline.
//
// Promise:
// - The day's group is drawn even when empty (where to drop, and that there is
//   nothing); the others only when they have cards. Cards outside the day's
//   group show their plan.
// - A card is carried with the pointer: a mouse picks it up once it moved a
//   few pixels; a finger holds it still for a moment first (until then the
//   list scrolls as usual). Letting go over the own lane puts a 30-minute
//   block where the pointer is; anywhere else nothing happens. A card that
//   was carried is not pressed.
// - Pressing a card lights the blocks linked to it for a moment and scrolls the
//   timeline to the first of them; it does not open the Case (a button that
//   shows on hover does). The button's room is kept, so nothing moves.
// - While a card is carried, "No date" is a place to drop it (drawn even when
//   empty): the Case or Action leaves every block and, for an Action, loses
//   its day; ⌘Z puts it back.
// - While a block's detail is open, the same Action's card has the block's color.

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import {
  type PointerEvent as ReactPointerEvent,
  useRef,
  useState,
} from "react";
import { useApi } from "../app/context";
import { PlanPopover } from "../cases/chain";
import { dateIn, formatMonthDay, planLabel } from "../cases/dates";
import { openCasesQuery } from "../cases/queries";
import { apiErrorMessage } from "../data/errors";
import { isOptimisticId } from "../data/optimistic";
import { LabelChip } from "../labels/chip";
import { type Label, useLabelsOf } from "../labels/queries";
import { useFormat } from "../me/profile";
import { Switch } from "../ui/bits";
import { ICON_BUTTON } from "../ui/button";
import { Caret, Icon } from "../ui/icon";
import { type DayItem, type DaySections, DROP_MINUTES, isoAt } from "./day";
import { useAddBlock, useLinkBlock, useUnassign } from "./queries";
import {
  blockAt,
  type FocusTarget,
  useCarry,
  useIsCarrying,
  useTimeBlockerScreen,
} from "./screen-state";

type Tab = "todo" | "done";

type CarryItem = {
  action: {
    caseId: string;
    caseTitle: string;
    id: string | null;
    title: string;
  };
};

export function DayLists({
  sections,
  selectedStepId,
}: {
  sections: DaySections;
  /** The Action whose block's detail is open. */
  selectedStepId: string | null;
}) {
  const screen = useTimeBlockerScreen();
  const [tab, setTab] = useState<Tab>("todo");
  const api = useApi();
  const format = useFormat();
  const [showActions, setShowActions] = useState(true);
  const caseTimes = (id: string) =>
    sections.placed
      .map((p) => p.block)
      .filter((b) => b.links.some((l) => l.caseId === id))
      .map((b) => `${format.time(b.startAt)}–${format.time(b.endAt)}`)
      .join(" · ");
  const cases = useQuery({
    ...openCasesQuery(api, null),
    enabled: tab === "todo" && !showActions,
  });
  // Groups folded on this screen (before, the day, after, no date).
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string) =>
    setClosed((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const { day, today } = screen;
  const isToday = day === today;
  const date = formatMonthDay(day);
  const groups = [
    {
      key: "before",
      label: isToday
        ? m.timeblocker_group_before_today()
        : m.timeblocker_group_before({ date }),
      items: sections.before,
    },
    {
      key: "on",
      label: isToday ? m.timeblocker_group_today() : date,
      items: sections.todo,
    },
    {
      key: "after",
      label: isToday
        ? m.timeblocker_group_after_today()
        : m.timeblocker_group_after({ date }),
      items: sections.after,
    },
    {
      key: "undated",
      label: m.timeblocker_group_undated(),
      items: sections.undated,
    },
  ];
  const tabs = [
    { key: "todo", label: m.timeblocker_todo(), count: undefined },
    { key: "done", label: m.timeblocker_done(), count: sections.done.length },
  ] as const;
  const carrying = useIsCarrying();
  const caseBlocks = (id: string) =>
    sections.placed
      .map((p) => p.block)
      .filter((b) => b.links.some((l) => l.caseId === id && !l.stepId))
      .map((b) => b.id);
  const card = (item: DayItem) => (
    <DayCard
      key={item.action.id}
      item={item}
      selected={item.action.id === selectedStepId}
    />
  );
  return (
    <section
      aria-label={m.timeblocker_lists()}
      className="relative flex min-h-0 flex-none flex-col border-border-soft max-md:max-h-[45%] max-md:border-b md:w-[300px] md:border-r"
    >
      <div className="flex flex-none items-center justify-between gap-2 px-4 pt-3">
        <div
          role="tablist"
          aria-label={m.timeblocker_tabs()}
          className="flex gap-1"
        >
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`h-7 cursor-pointer rounded-[14px] px-3 text-[12.5px] ${
                tab === t.key
                  ? "bg-hover text-fg"
                  : "text-faint hover:text-body"
              }`}
            >
              {t.label}
              {t.count !== undefined && (
                <span className="ml-1.5 tabular-nums text-faint">
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[12px] text-muted">
          <span>Action</span>
          <Switch
            label={m.timeblocker_show_actions()}
            checked={showActions}
            onChange={setShowActions}
          />
        </div>
      </div>
      <div
        role="tabpanel"
        // Not `panel-scroll`: on a phone the timeline below it, not the list, reaches the Dock.
        className="min-h-0 flex-1 overflow-y-auto px-3 pt-2 pb-4"
      >
        {tab === "todo" && !showActions ? (
          cases.isError ? (
            <p className="px-2 py-3 text-[12.5px] text-muted">
              {apiErrorMessage(cases.error, m.timeblocker_load_failed())}
            </p>
          ) : (
            <>
              {cases.data?.items.length === 0 && (
                <p className="px-2 py-3 text-[12.5px] text-faint">
                  {m.cases_empty()}
                </p>
              )}
              <ul>
                {cases.data?.items.map((c) => (
                  <li key={c.id}>
                    <div className="mb-2">
                      <CaseCard
                        id={c.id}
                        blockIds={caseBlocks(c.id)}
                        title={c.title}
                        subtitle={caseTimes(c.id)}
                        labelIds={c.labelIds}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )
        ) : tab === "todo" ? (
          groups.map((g) =>
            g.key !== "on" &&
            g.items.length === 0 &&
            !(g.key === "undated" && carrying) ? null : (
              <GroupSlot key={g.key} undated={g.key === "undated"}>
                <GroupHead
                  label={g.label}
                  count={g.items.length}
                  open={!closed.has(g.key)}
                  onToggle={() => toggle(g.key)}
                />
                {!closed.has(g.key) &&
                  (g.items.length === 0 ? (
                    <p className="px-2 pt-1 pb-2 text-[12.5px] text-faint">
                      {g.key === "undated"
                        ? m.timeblocker_undated_drop()
                        : m.timeblocker_empty_todo()}
                    </p>
                  ) : (
                    <ul>
                      {Array.from(
                        Map.groupBy(g.items, (i) => i.action.caseId),
                        ([id, items]) => (
                          <li key={id} className="mb-2">
                            <CaseCard
                              id={id}
                              blockIds={caseBlocks(id)}
                              title={items[0].action.caseTitle}
                              subtitle={caseTimes(id)}
                              labelIds={items[0].action.labelIds}
                            />
                            <ul className="space-y-1 pt-1 pl-1">
                              {items.map((i) => card(i))}
                            </ul>
                          </li>
                        ),
                      )}
                    </ul>
                  ))}
              </GroupSlot>
            ),
          )
        ) : sections.done.length === 0 ? (
          <p className="px-2 py-3 text-[12.5px] text-faint">
            {m.timeblocker_empty_done()}
          </p>
        ) : (
          <ul>
            {showActions
              ? sections.done.map((i) => card(i))
              : Array.from(
                  Map.groupBy(sections.done, (i) => i.action.caseId),
                  ([id, items]) => (
                    <li key={id} className="mb-2">
                      <CaseCard
                        id={id}
                        blockIds={caseBlocks(id)}
                        title={items[0].action.caseTitle}
                        subtitle={caseTimes(id)}
                        labelIds={items[0].action.labelIds}
                      />
                    </li>
                  ),
                )}
          </ul>
        )}
      </div>
    </section>
  );
}

/** Lights the blocks linked to a pressed card and scrolls the timeline to the first. */
function useLightBlocks() {
  const screen = useTimeBlockerScreen();
  return (target: FocusTarget, blockIds: string[]) => {
    screen.setFocus(target);
    for (const id of blockIds) {
      const el = document.querySelector(`[data-block-id="${CSS.escape(id)}"]`);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        break;
      }
    }
  };
}

/** The button at a card's right that opens its Case; its room is always kept. */
function OpenCaseButton({ caseId }: { caseId: string }) {
  const navigate = useNavigate();
  // Not saved yet: only the room is kept, so nothing moves when it is.
  if (isOptimisticId(caseId))
    return (
      <span
        aria-hidden="true"
        className="-my-[3px] h-[26px] w-[26px] flex-none"
      />
    );
  return (
    <button
      type="button"
      data-no-carry=""
      aria-label={m.timeblocker_open_case()}
      title={m.timeblocker_open_case()}
      onClick={(e) => {
        e.stopPropagation();
        void navigate({ to: `/me/cases/${caseId}` });
      }}
      className={`${ICON_BUTTON} -my-[3px] opacity-0 group-hover/card:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100`}
    >
      <Icon kind="open" size={14} />
    </button>
  );
}

/** The labels' chips on a line of their own under a card's text (more lines when many). */
function CardLabels({ labels }: { labels: readonly Label[] }) {
  if (labels.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 py-px">
      {labels.map((l) => (
        <LabelChip key={l.id} label={l} />
      ))}
    </div>
  );
}

/** A card's text under its title (never cut short), then the Case's labels as chips. */
function Subline({
  text,
  labelIds,
}: {
  text: string;
  labelIds: readonly string[];
}) {
  const labels = useLabelsOf(labelIds);
  if (!text && labels.length === 0) return null;
  return (
    <>
      {text && (
        <div className="text-[11.5px] leading-[18px] text-faint">{text}</div>
      )}
      <CardLabels labels={labels} />
    </>
  );
}

function CaseCard({
  id,
  blockIds,
  title,
  subtitle,
  labelIds,
}: {
  id: string;
  /** The blocks linked to the Case (lit when the card is pressed). */
  blockIds: string[];
  title: string;
  subtitle: string;
  labelIds: readonly string[];
}) {
  const light = useLightBlocks();
  const carry = useCarryCard(
    { action: { caseId: id, caseTitle: title, id: null, title } },
    !isOptimisticId(id),
  );
  return (
    <div
      {...carry}
      className="group/card touch-pan-y select-none rounded-[6px] px-2 py-1 text-[12.5px] text-muted hover:bg-hover cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-start gap-2">
        <Icon
          kind="cases"
          size={16}
          className="mt-0.5 flex-none pointer-events-none text-muted"
        />
        <button
          type="button"
          onClick={() => light({ caseId: id, stepId: null }, blockIds)}
          className="block min-w-0 flex-1 cursor-grab text-left active:cursor-grabbing"
        >
          <div className="truncate text-[13.5px] leading-5 text-fg">
            {title}
          </div>
          <Subline text={subtitle} labelIds={labelIds} />
        </button>
        <OpenCaseButton caseId={id} />
      </div>
    </div>
  );
}

/** A group of the list; "No date" is a place to drop a card. */
function GroupSlot({
  undated,
  children,
}: {
  undated: boolean;
  children: React.ReactNode;
}) {
  return undated ? (
    <UndatedBox>{children}</UndatedBox>
  ) : (
    <div className="mb-2 rounded-[10px] border border-transparent">
      {children}
    </div>
  );
}

/**
 * "No date" is also where a carried card is dropped to
 * take it off its blocks: while a card is carried it is drawn (even when
 * empty) as that place, lit while the pointer is over it. It keeps one
 * element all the while, so the cards in it are not made again mid-carry.
 */
function UndatedBox({ children }: { children: React.ReactNode }) {
  const carry = useCarry();
  const ref = useRef<HTMLDivElement>(null);
  const carrying = carry !== null;
  const rect = carrying ? ref.current?.getBoundingClientRect() : undefined;
  const over =
    !!carry &&
    !!rect &&
    carry.x >= rect.left &&
    carry.x <= rect.right &&
    carry.y >= rect.top &&
    carry.y <= rect.bottom;
  return (
    <div
      ref={ref}
      data-group="undated"
      data-drop-undated={carrying ? "" : undefined}
      className={`mb-2 rounded-[10px] border ${
        carrying
          ? `border-dashed ${over ? "border-accent bg-chosen" : "border-field-strong"}`
          : "border-transparent"
      }`}
    >
      {children}
    </div>
  );
}

/** A group's heading (pressing it folds the group). */
function GroupHead({
  label,
  count,
  open,
  onToggle,
}: {
  label: string;
  count: number;
  open: boolean;
  onToggle(): void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="flex w-full cursor-pointer items-center gap-1 rounded-[6px] px-2 py-1 text-left text-[12px] text-muted hover:bg-hover"
    >
      <Caret open={open} />
      <span className="flex-1">{label}</span>
      <span className="tabular-nums">{count}</span>
    </button>
  );
}

/** A finger holds a card this long before it is picked up. */
const HOLD_MS = 450;
/** A mouse picks a card up after moving this far; a finger moving this far scrolls instead. */
const MOUSE_SLOP = 4;
const TOUCH_SLOP = 8;

const stopScroll = (e: TouchEvent) => e.preventDefault();

/**
 * Carrying a card onto the timeline with the pointer (not the HTML5 drag,
 * which touch screens do not have; the same build as moving a block).
 */
function useCarryCard(item: CarryItem, enabled: boolean) {
  const screen = useTimeBlockerScreen();
  const addBlock = useAddBlock();
  const linkBlock = useLinkBlock();
  const unassign = useUnassign();
  const onError = (err: unknown) =>
    screen.setError(apiErrorMessage(err, m.timeblocker_save_failed()));
  const press = useRef<{
    id: number;
    x: number;
    y: number;
    touch: boolean;
    lifted: boolean;
    timer: number;
    el: HTMLElement;
    caseOnly: boolean;
    /** Picked up from "No date" (dropping it back there changes nothing). */
    fromUndated: boolean;
  } | null>(null);
  /** A carried card's click must not open its Case. */
  const carried = useRef(false);

  const lift = (x: number, y: number) => {
    const p = press.current;
    if (!p) return;
    p.lifted = true;
    carried.current = true;
    try {
      p.el.setPointerCapture(p.id);
    } catch {
      // The pointer may be gone already (a cancelled touch).
    }
    // A finger that picked it up no longer scrolls the list.
    if (p.touch)
      document.addEventListener("touchmove", stopScroll, { passive: false });
    move(x, y);
  };
  const move = (x: number, y: number) => {
    screen.edgeScroll.current?.(y);
    screen.carry.set({
      item,
      caseOnly: press.current?.caseOnly ?? false,
      x,
      y,
      minute: screen.hitMinute.current?.(x, y) ?? null,
      overBlock: blockAt(x, y),
    });
  };
  const end = (drop: boolean) => {
    const p = press.current;
    press.current = null;
    if (!p) return;
    window.clearTimeout(p.timer);
    if (p.touch) document.removeEventListener("touchmove", stopScroll);
    if (!p.lifted) return;
    const carried = screen.carry.get();
    const at = carried?.minute ?? null;
    const blockId = carried
      ? document
          .elementFromPoint(carried.x, carried.y)
          ?.closest("[data-block-id]")
          ?.getAttribute("data-block-id")
      : null;
    const target = {
      caseId: item.action.caseId,
      ...(p.caseOnly || !item.action.id ? {} : { stepId: item.action.id }),
    };
    const undated =
      carried &&
      document
        .elementFromPoint(carried.x, carried.y)
        ?.closest("[data-drop-undated]");
    screen.carry.set(null);
    if (!drop) return;
    if (undated && !p.fromUndated) {
      unassign.mutate({ ...screen.place, target }, { onError });
      return;
    }
    if (blockId) {
      const start = carried
        ? document
            .elementFromPoint(carried.x, carried.y)
            ?.closest("[data-block-start]")
            ?.getAttribute("data-block-start")
        : null;
      linkBlock.mutate(
        {
          ...screen.place,
          day: start ? dateIn(start, screen.timeZone) : screen.day,
          blockId,
          target,
        },
        { onError },
      );
      return;
    }
    if (at === null) return;
    addBlock.mutate(
      {
        ...screen.place,
        links: [target],
        startAt: isoAt(screen.dayStart, at),
        endAt: isoAt(screen.dayStart, at + DROP_MINUTES),
      },
      { onError },
    );
  };

  return {
    onPointerDown(e: ReactPointerEvent<HTMLElement>) {
      if (!enabled || e.button !== 0) return;
      if ((e.target as Element).closest("input, [data-no-carry]")) return;
      carried.current = false;
      const touch = e.pointerType !== "mouse";
      if (!touch) {
        const target =
          e.target instanceof HTMLElement ? e.target : e.currentTarget;
        target.setPointerCapture(e.pointerId);
      }
      const x = e.clientX;
      const y = e.clientY;
      press.current = {
        id: e.pointerId,
        caseOnly:
          !item.action.id ||
          !!(e.target as Element).closest("[data-carry-case]"),
        fromUndated: !!e.currentTarget.closest('[data-group="undated"]'),
        x,
        y,
        touch,
        lifted: false,
        el: e.currentTarget,
        timer: touch ? window.setTimeout(() => lift(x, y), HOLD_MS) : 0,
      };
    },
    onPointerMove(e: ReactPointerEvent<HTMLElement>) {
      const p = press.current;
      if (!p || p.id !== e.pointerId) return;
      if (p.lifted) {
        move(e.clientX, e.clientY);
        return;
      }
      const far = Math.hypot(e.clientX - p.x, e.clientY - p.y);
      if (p.touch) {
        // Moving before the hold is scrolling.
        if (far > TOUCH_SLOP) end(false);
      } else if (far > MOUSE_SLOP) lift(e.clientX, e.clientY);
    },
    onPointerUp: () => end(true),
    onPointerCancel: () => end(false),
    onContextMenu(e: ReactPointerEvent<HTMLElement> | React.MouseEvent) {
      // A finger's hold would open the system's menu over the card.
      if (press.current?.touch) e.preventDefault();
    },
    onClickCapture(e: React.MouseEvent) {
      if (!carried.current) return;
      carried.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
  };
}

/** One Action card: lights its blocks, changes its plan, and is carried onto the timeline. */
function DayCard({
  item,
  selected,
}: {
  item: DayItem;
  /** The Action whose block's detail is open (the block's color). */
  selected: boolean;
}) {
  const screen = useTimeBlockerScreen();
  const light = useLightBlocks();
  const format = useFormat();
  const unassign = useUnassign();
  const anchor = useRef<HTMLSpanElement>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const { action } = item;
  const open = action.status === "open";
  const pending = isOptimisticId(action.id);
  const carry = useCarryCard(item, open && !pending);
  const onError = (err: unknown) =>
    screen.setError(apiErrorMessage(err, m.timeblocker_save_failed()));
  // An action with no day says nothing (the lack of a date is not shown).
  const planText = planLabel({ ...action, kind: "action" }, screen.today) ?? "";
  const assignedTimes = item.blocks
    .map((b) => `${format.time(b.startAt)}–${format.time(b.endAt)}`)
    .join(" · ");
  const subtitle = assignedTimes || planText;
  const labels = useLabelsOf(action.labelIds);
  const carriable = open && !pending;
  return (
    <li
      aria-current={selected ? "true" : undefined}
      {...carry}
      className={`group/card flex touch-pan-y select-none items-start gap-2 rounded-[8px] px-2 py-[7px] [-webkit-touch-callout:none] ${
        selected
          ? "bg-chosen"
          : "bg-hover/50 hover:bg-hover has-[[aria-expanded=true]]:bg-hover"
      } ${carriable ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      <Icon
        kind="action"
        size={16}
        className="mt-0.5 flex-none pointer-events-none text-muted"
      />
      <button
        type="button"
        onClick={() =>
          light(
            { caseId: action.caseId, stepId: action.id },
            item.blocks.map((b) => b.id),
          )
        }
        className={`block min-w-0 flex-1 text-left ${carriable ? "cursor-grab active:cursor-grabbing" : ""}`}
      >
        <div className="truncate text-[13.5px] leading-[20px] text-fg">
          {action.title}
        </div>
        {(!open || subtitle) && (
          <div className="text-[11.5px] leading-[17px] text-faint">
            {!open && <span data-carry-case="">{action.caseTitle}</span>}
            {subtitle ? `${!open ? " · " : ""}${subtitle}` : null}
          </div>
        )}
        <CardLabels labels={labels} />
      </button>
      {!pending && (
        <span data-no-carry="" className="flex flex-none">
          <OpenCaseButton caseId={action.caseId} />
          {open && (
            <span
              ref={anchor}
              className={`flex flex-none group-hover/card:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100 ${
                planOpen ? "opacity-100" : "opacity-0"
              }`}
            >
              <button
                type="button"
                aria-label={m.cases_change_plan()}
                aria-haspopup="menu"
                aria-expanded={planOpen}
                onClick={() => setPlanOpen(true)}
                className={`${ICON_BUTTON} -my-[3px]`}
              >
                <Icon kind="more" size={14} />
              </button>
              <PlanPopover
                anchor={anchor}
                align="start"
                minimal
                heading={m.timeblocker_plan()}
                open={planOpen}
                onClose={() => setPlanOpen(false)}
                step={action}
                today={screen.today}
                timeZone={screen.timeZone}
                onChange={(plan) =>
                  unassign.mutate(
                    {
                      ...screen.place,
                      target: { caseId: action.caseId, stepId: action.id },
                      plan,
                    },
                    { onError },
                  )
                }
              />
            </span>
          )}
        </span>
      )}
    </li>
  );
}
